import React from "react";
import { Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/system";

const AutobiaTooltip = ({
  title,
  children,
  placement = "top",
  arrow = true,
  enterDelay = 500,
  leaveDelay = 200,
  maxWidth = 300,
  color = "dark",
  size = "medium",
  disabled = false,
}) => {
  const theme = useTheme();

  const getTooltipStyles = () => {
    const styles = {
      dark: {
        backgroundColor: theme.palette.gray.g800,
        color: "white",
      },
      light: {
        backgroundColor: "white",
        color: theme.palette.gray.g700,
        border: `1px solid ${theme.palette.gray.g200}`,
        boxShadow: theme.shadows[2],
      },
      primary: {
        backgroundColor: theme.palette.primary.main,
        color: "white",
      },
      secondary: {
        backgroundColor: theme.palette.secondary.main,
        color: "white",
      },
      error: {
        backgroundColor: theme.palette.error.main,
        color: "white",
      },
      warning: {
        backgroundColor: theme.palette.warning.main,
        color: "white",
      },
      success: {
        backgroundColor: theme.palette.success.main,
        color: "white",
      },
    };
    return styles[color] || styles.dark;
  };

  const tooltipStyles = getTooltipStyles();
  
  const fontSize = {
    small: "0.75rem",
    medium: "0.8rem",
    large: "0.875rem",
  };

  const padding = {
    small: "4px 8px",
    medium: "6px 12px", 
    large: "8px 16px",
  };

  // If disabled, just return children without tooltip
  if (disabled) {
    return children;
  }

  const tooltipContent = typeof title === "string" ? (
    <Typography
      variant="body2"
      sx={{
        fontSize: fontSize[size],
        fontWeight: 400,
        lineHeight: 1.4,
      }}
    >
      {title}
    </Typography>
  ) : (
    title
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement={placement}
      arrow={arrow}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      componentsProps={{
        tooltip: {
          sx: {
            ...tooltipStyles,
            borderRadius: "8px",
            padding: padding[size],
            maxWidth: maxWidth,
            fontSize: fontSize[size],
            fontFamily: theme.typography.fontFamily,
            "& .MuiTooltip-arrow": {
              color: tooltipStyles.backgroundColor,
              ...(color === "light" && {
                "&:before": {
                  border: `1px solid ${theme.palette.gray.g200}`,
                },
              }),
            },
          },
        },
        popper: {
          sx: {
            zIndex: theme.zIndex.tooltip,
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

// Tooltip with rich content support
export const AutobiaTooltipRich = ({
  title,
  subtitle,
  children,
  placement = "top",
  arrow = true,
  color = "dark",
  maxWidth = 300,
}) => {
  const content = (
    <div>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: subtitle ? 0.5 : 0 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          {subtitle}
        </Typography>
      )}
    </div>
  );

  return (
    <AutobiaTooltip
      title={content}
      placement={placement}
      arrow={arrow}
      color={color}
      maxWidth={maxWidth}
      enterDelay={300}
      size="medium"
    >
      {children}
    </AutobiaTooltip>
  );
};

export default AutobiaTooltip;